import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import PageTitle from "../../components/PageTitle";
import { Grid } from "@mui/material";
import PracticeSection from "../dashboard/components/PracticeSection";
import Button from "../../components/Button";
import useStyles from "../dashboard/styles";
import Modal from "../../components/Modal";
import Select from "react-select";
// import InputGroup from "../../components/FormComponent/InputGroup";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import formStyle from "../../components/FormComponent/FormStyles.module.css";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";

const MyLearning = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [quickPracticeModalOpen, setQuickPracticeModalOpen] = useState(false);
  const [takeQuizModalOpen, setTakeQuizModalOpen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);

  const [examTypes, setExamTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [years, setYears] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);

  const [formData, setFormData] = useState({
    quizType: "subject",
    examType: "",
    subject: "",
    year: "",
    mode: "practice",
    answerMode: "instant",
    numberOfQuestions: "",
    timeLimit: 30
  });

  const [loading, setLoading] = useState(false);
  const [maxQuestions, setMaxQuestions] = useState(0);
  const [isCounting, setIsCounting] = useState(false);

  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const res = await httprequest.get("/api/v1/exam-types");
        if (res?.data?.data) {
          setExamTypes(res.data.data.filter(type => type !== "PRIMARY"));
        }
      } catch (err) {
        // console.error(err);
      }
    };
    fetchExamTypes();
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!formData.examType) return;
      try {
        const res = await httprequest.get(`/api/v1/exam-types/${formData.examType}/subjects`);
        if (res?.data?.data) {
          setSubjects(res.data.data);
        }
      } catch (err) {
        // console.error(err);
      }
    };
    fetchSubjects();
  }, [formData.examType]);

  useEffect(() => {
    const fetchSubjectData = async () => {
      if (!formData.subject) return;
      try {
        const [yearsRes, topicsRes] = await Promise.all([
          httprequest.get(`/api/v1/subjects/${formData.subject}/years${formData.examType ? `?exam_type=${formData.examType}` : ""}`),
          httprequest.get(`/api/v1/topics/categories?subject=${formData.subject}`)
        ]);
        if (yearsRes?.data?.data) {
          const fetchedYears = yearsRes.data.data;
          setYears(fetchedYears);
          if (fetchedYears.length > 0 && !formData.year) {
            setFormData(prev => ({ ...prev, year: fetchedYears[0] }));
          }
        }
        if (topicsRes?.data?.data) {
          setTopicOptions(topicsRes.data.data.map(topic => {
            const isObject = typeof topic === "object" && topic !== null;

            return {
              value: isObject ? (topic.topic_category_slug || topic.topic_category) : topic,
              label: isObject ? (topic.topic_category || topic.topic_category_slug) : topic
            };
          }));
        }
      } catch (err) {
        // console.error(err);
      }
    };
    fetchSubjectData();
  }, [formData.subject, formData.examType]);

  useEffect(() => {
    const checkQuestionCount = async () => {
      if (formData.quizType === "random") {
        setMaxQuestions(100);

        return;
      }

      if (!formData.examType || !formData.subject) {
        setMaxQuestions(0);

        return;
      }

      setIsCounting(true);
      try {
        let qs = `?exam_type=${formData.examType}&subject=${formData.subject}`;
        if (formData.year) qs += `&year=${formData.year}`;
        if (formData.difficulty) qs += `&difficulty=${formData.difficulty}`;
        if (selectedTopics.length > 0) {
          qs += `&topic_category=${selectedTopics.map(t => t.value).join(",")}`;
        }

        const res = await httprequest.get(`/api/v1/questions/count${qs}`);
        if (res?.data?.data?.total_count != null) {
          const count = res.data.data.total_count;
          setMaxQuestions(count);
          if (formData.numberOfQuestions > count) {
            setFormData(prev => ({ ...prev, numberOfQuestions: count || 1 }));
          }
        }
      } catch (err) {
        // console.error("Count check failed", err);
      } finally {
        setIsCounting(false);
      }
    };

    const timeoutId = setTimeout(() => {
      checkQuestionCount();
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [formData.quizType, formData.examType, formData.subject, formData.year, formData.difficulty, selectedTopics]);

  const resetForm = () => {
    setFormData({
      quizType: "subject",
      examType: "",
      subject: "",
      year: "",
      mode: "practice",
      answerMode: "instant",
      numberOfQuestions: "",
      timeLimit: 30
    });
    setSelectedTopics([]);
    setSubjects([]);
    setYears([]);
    setTopicOptions([]);
    setMaxQuestions(0);
  };

  const toggleModal = () => {
    if (quickPracticeModalOpen) resetForm();
    setQuickPracticeModalOpen(!quickPracticeModalOpen);
  };

  const toggleTakeQuizModal = () => {
    if (takeQuizModalOpen) resetForm();
    setTakeQuizModalOpen(!takeQuizModalOpen);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStartPractice = async () => {
    if (!formData.examType || !formData.subject) {
      toast.error("Please select an Exam Type and Subject.");

      return;
    }

    if (maxQuestions === 0) {
      toast.error("No questions available for this selection.");

      return;
    }

    try {
      setLoading(true);
      const payload = {
        exam_type: formData.examType,
        subject: formData.subject,
        mode: formData.mode,
        answer_mode: formData.answerMode || "instant",
        question_count: parseInt(formData.numberOfQuestions) || 10
      };

      if (formData.year) {
        payload.year = parseInt(formData.year);
      }
      if (formData.difficulty) {
        payload.difficulty = formData.difficulty;
      }
      if (formData.mode === "exam") {
        payload.time_limit = formData.timeLimit * 60;
      }
      if (selectedTopics.length > 0) {
        payload.topic_category = selectedTopics.map(t => t.value).join(",");
      }

      const res = await httprequest.post("/api/v1/practice-sessions", payload);
      const sessionId = res?.data?.data?.session?.id || res?.data?.data?.session_id;

      if (sessionId) {
        toast.success("Session created successfully.");
        navigate(`/student/my-learning/practice/${sessionId}`);
      } else {
        toast.error("Failed to create session. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (formData.quizType === "subject" && (!formData.examType || !formData.subject)) {
      toast.error("Please select an Exam Type and Subject.");

      return;
    }

    if (maxQuestions === 0) {
      toast.error("No questions available for this selection.");

      return;
    }

    try {
      setLoading(true);
      const payload = {
        quiz_type: formData.quizType,
        question_count: parseInt(formData.numberOfQuestions) || 10,
        mode: "practice",
        answer_mode: "end"
      };

      if (formData.difficulty) payload.difficulty = formData.difficulty;

      if (formData.quizType === "subject") {
        payload.exam_type = formData.examType;
        payload.subject = formData.subject;
        if (formData.year) payload.year = parseInt(formData.year);
      }

      const res = await httprequest.post("/api/v1/practice-sessions", payload);
      const sessionId = res?.data?.data?.session?.id || res?.data?.data?.session_id;

      if (sessionId) {
        toast.success("Quiz created successfully.");
        navigate(`/student/my-learning/practice/${sessionId}`);
      } else {
        toast.error("Failed to create quiz. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const renderTakeQuizForm = () => {
    const isRandom = formData.quizType === "random";

    return (
      <Grid container spacing={2} style={{ padding: "10px" }}>
        <Grid item xs={12} sm={6}>
          <SelectGroup label="Quiz Type">
            <select className={formStyle.input} name="quizType" value={formData.quizType} onChange={handleChange}>
              <option value="subject">Subject</option>
              <option value="random">Random</option>
            </select>
          </SelectGroup>
        </Grid>

        {!isRandom && (
          <>
            <Grid item xs={12} sm={6}>
              <SelectGroup label="Exam Type">
                <select className={formStyle.input} name="examType" value={formData.examType} onChange={handleChange}>
                  <option value="">Select Exam</option>
                  {examTypes.map((type, id) => (
                    <option key={id} value={type}>{type}</option>
                  ))}
                </select>
              </SelectGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectGroup label="Subject">
                <select className={formStyle.input} name="subject" value={formData.subject} onChange={handleChange} disabled={!formData.examType}>
                  <option value="">Select Subject</option>
                  {subjects.map((sub, id) => (
                    <option key={id} value={sub.subject_slug}>{sub.subject}</option>
                  ))}
                </select>
              </SelectGroup>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <SelectGroup label="Difficulty">
            <select className={formStyle.input} name="difficulty" value={formData.difficulty || ""} onChange={handleChange}>
              <option value="">Select Difficulty</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </SelectGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SelectGroup label={`Number of Questions${!isRandom && maxQuestions > 0 ? ` (Max: ${maxQuestions})` : ""}`}>
            <input
              type="number"
              name="numberOfQuestions"
              value={formData.numberOfQuestions}
              onChange={handleChange}
              className={formStyle.input}
              min="1"
              max={!isRandom && maxQuestions > 0 ? maxQuestions : 100}
              disabled={!isRandom && (maxQuestions === 0 || isCounting)}
            />
            {!isRandom && maxQuestions === 0 && !isCounting && formData.subject && (
              <p style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>
                No questions available for this selection.
              </p>
            )}
          </SelectGroup>
        </Grid>

        <Grid item xs={12}>
          <div style={{ marginTop: "20px" }}>
            <Button
              variant="primary"
              buttonName={loading ? "Creating..." : "Continue"}
              buttonSize="full"
              color="btndefault"
              onClick={handleStartQuiz}
              disabled={loading || isCounting || !formData.numberOfQuestions || (!isRandom && maxQuestions === 0)}
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  const renderQuickPracticeForm = () => {
    return (
      <Grid container spacing={2} style={{ padding: "10px" }}>
        <Grid item xs={12} sm={6}>
          <SelectGroup label="Exam Type">
            <select className={formStyle.input} name="examType" value={formData.examType} onChange={handleChange}>
              <option value="">Select Exam</option>
              {examTypes.map((type, id) => (
                <option key={id} value={type}>{type}</option>
              ))}
            </select>
          </SelectGroup>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectGroup label="Subject">
            <select className={formStyle.input} name="subject" value={formData.subject} onChange={handleChange} disabled={!formData.examType}>
              <option value="">Select Subject</option>
              {subjects.map((sub, id) => (
                <option key={id} value={sub.subject_slug}>{sub.subject}</option>
              ))}
            </select>
          </SelectGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SelectGroup label="Year">
            <select className={formStyle.input} name="year" value={formData.year} onChange={handleChange} disabled={years.length === 0}>
              {years.map((year, id) => (
                <option key={id} value={year}>{year}</option>
              ))}
            </select>
          </SelectGroup>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectGroup label="Mode">
            <select className={formStyle.input} name="mode" value={formData.mode} onChange={handleChange}>
              <option value="exam">Exam</option>
              <option value="practice">Practice</option>
            </select>
          </SelectGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SelectGroup label="Answer Mode">
            <select className={formStyle.input} name="answerMode" value={formData.answerMode} onChange={handleChange}>
              <option value="instant">Instant Feedback</option>
              <option value="end">Review at End</option>
            </select>
          </SelectGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SelectGroup label={`Number of Question ${maxQuestions > 0 ? `(Max: ${maxQuestions})` : ""}`}>
            <input
              type="number"
              name="numberOfQuestions"
              value={formData.numberOfQuestions}
              onChange={handleChange}
              className={formStyle.input}
              min="1"
              max={maxQuestions > 0 ? maxQuestions : ""}
              disabled={maxQuestions === 0 || isCounting}
            />
            {maxQuestions === 0 && !isCounting && formData.subject && (
              <p style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", width: "200%" }}>
                No questions available for this selection.
              </p>
            )}
          </SelectGroup>
        </Grid>

        {formData.mode === "exam" && (
          <Grid item xs={12} sm={6}>
            <SelectGroup label="Time (Mins)">
              <select className={formStyle.input} name="timeLimit" value={formData.timeLimit} onChange={handleChange}>
                <option value="10">10 mins</option>
                <option value="30">30 mins</option>
                <option value="60">60 mins</option>
                <option value="120">120 mins</option>
              </select>
            </SelectGroup>
          </Grid>
        )}

        <Grid item xs={12}>
          <div className={formStyle.inputwrapper}>
            <p>Topic</p>
            <div style={{ marginTop: "4px" }}>
              <Select
                isMulti
                name="topics"
                options={topicOptions}
                className="basic-multi-select"
                classNamePrefix="select"
                value={selectedTopics}
                onChange={setSelectedTopics}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div style={{ marginTop: "20px" }}>
            <Button
              variant="primary"
              buttonName={loading ? "Creating..." : "Continue"}
              buttonSize="full"
              color="btndefault"
              onClick={handleStartPractice}
              disabled={loading || isCounting || !formData.numberOfQuestions || maxQuestions === 0}
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <Layout
      children={
        <>
          <PageTitle
            title="My Learning"
            subtitle="Access your practice exams, materials, and learning history"
          />
          <Grid container spacing={2} style={{ marginBottom: "20px", marginTop: "24px" }}>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <div className={classes.learningTopCard}>
                <h4>Quick Practice</h4>
                <p>Form Random Questions To practice with</p>
                <div style={{ width: "120px" }}>
                  <Button
                    variant="primary"
                    buttonName="Start"
                    buttonSize="full"
                    color="btndefault"
                    onClick={toggleModal}
                  />
                </div>
              </div>
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <div className={classes.learningTopCard}>
                <h4>Take a quiz</h4>
                <p>Challenge yourself</p>
                <div style={{ width: "120px" }}>
                  <Button
                    variant="primary"
                    buttonName="Start"
                    buttonSize="full"
                    color="btndefault"
                    onClick={toggleTakeQuizModal}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: "16px" }}>
            <Grid item xs={12}>
              <div>
                <PracticeSection />
              </div>
            </Grid>
          </Grid>

          <Modal
            isopen={quickPracticeModalOpen}
            closeModal={toggleModal}
            title="Quick Practice"
            subtitle="Choose your exam, filter questions, and practice at your own pace"
            modalContent={renderQuickPracticeForm()}
          />

          <Modal
            isopen={takeQuizModalOpen}
            closeModal={toggleTakeQuizModal}
            title="Take a quiz"
            subtitle="Choose what quiz you want to try out"
            modalContent={renderTakeQuizForm()}
          />
        </>
      }
    />
  );
};

export default MyLearning;
